import React, { useRef } from "react";
import * as Yup from "yup";
import { useFormikContext } from "formik";
import {
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  VStack,
  Error,
} from "../../../../components/utils";
import RepricingReportDownload from "./RepricingReportDownload";
import {
  Input,
  Label,
  TextArea,
} from "../../../../components/shared/InputField";
import { Select } from "../../../../components/shared/Select";
import GenericFilterBar, { GenericFilterType } from "../../utils/Filters";
import InfoBox from "../../../../components/shared/InfoBox";
import { OptionsRepricingDetails } from "../../../../types/CompanyProfile";
import {
  EmploymentStatus,
  EmploymentType,
  UploadedFile,
} from "../../../../types/Transaction";
import { GrantState } from "../../../../types/common";
import { useEsopPlans } from "../../../../queries/esopPlan";

const REPRICING_REASONS = [
  "Market Decline",
  "Employee Retention",
  "Alignment with Market Value",
  "Company Performance",
  "Other",
];

type Props = {
  validationSchema: Yup.ObjectSchema<any>;
  onStepChange: (step?: number) => void;
  onClose: () => void;
  isLoading?: boolean;
  readonly?: boolean;
  s3url?: string;
};

function SupportingDocumentsField({ readonly }: { readonly?: boolean }) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docs: UploadedFile[] = formik.values.supportingDocuments ?? [];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const uploaded: UploadedFile = {
      name: file.name,
      type: file.type,
      size: file.size,
      base64,
      isUserUploaded: true,
    };
    formik.setFieldValue("supportingDocuments", [...docs, uploaded]);
    e.target.value = "";
  };

  const handleRemove = (index: number) => {
    formik.setFieldValue(
      "supportingDocuments",
      docs.filter((_, i) => i !== index)
    );
  };

  return (
    <VStack className="gap-1">
      <Label className="text-sm font-normal">
        Supporting Documents <span className="text-gray-400">(Optional)</span>
      </Label>
      {!readonly && (
        <>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="self-start px-3 py-1.5 text-sm border border-orange-500 text-orange-500 rounded hover:bg-orange-50"
          >
            Choose File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc"
            className="hidden"
            onChange={handleFileChange}
          />
          <p className="text-xs text-gray-400">PDF or DOCX, max 10 MB</p>
        </>
      )}
      {docs.length === 0 && readonly && (
        <p className="text-sm text-gray-400">No documents</p>
      )}
      {docs.map((doc, i) => (
        <HStack key={i} className="items-center gap-2">
          <a
            href={doc.base64 || doc.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-500 hover:underline"
            download={doc.name}
          >
            {doc.name}
          </a>
          {!readonly && (
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="text-xs text-red-400 hover:text-red-600"
            >
              Remove
            </button>
          )}
        </HStack>
      ))}
    </VStack>
  );
}

export default function BasicRepricingDetails({
  validationSchema,
  onStepChange,
  onClose,
  isLoading,
  readonly,
  s3url,
}: Props) {
  const formik = useFormikContext<OptionsRepricingDetails>();
  const { data: esopPlans } = useEsopPlans(true);

  async function handleNext() {
    const errors = await formik.validateForm();
    formik.setTouched({
      name: true,
      date: true,
      newStrikePrice: true,
      repricingType: true,
    });
    if (Object.keys(errors).length === 0) {
      onStepChange();
    }
  }

  const filters: GenericFilterType[] = [];

  if (esopPlans && esopPlans.length > 0) {
    filters.push({
      label: "Plans",
      id: "planIds",
      options: esopPlans.map((p) => ({ id: p.esopPlanId, name: p.planName })),
      type: "multiCheckboxNameAndId",
      value: formik.values.filters?.planIds || [],
      onChange: (selectedValues: any) => {
        formik.setFieldValue("filters.planIds", selectedValues);
      },
    });
  }

  filters.push({
    label: "Employment Status",
    id: "employmentStatus",
    options: Object.values(EmploymentStatus),
    type: "multiCheckbox",
    value: formik.values.filters?.employmentStatus || [],
    onChange: (selectedValues: string[]) => {
      formik.setFieldValue("filters.employmentStatus", selectedValues);
    },
  });

  filters.push({
    label: "Employment Type",
    id: "employmentType",
    options: Object.values(EmploymentType),
    type: "multiCheckbox",
    value: formik.values.filters?.employmentType || [],
    onChange: (selectedValues: string[]) => {
      formik.setFieldValue("filters.employmentType", selectedValues);
    },
  });

  filters.push({
    label: "Grant State",
    id: "grantState",
    options: [GrantState.GRANTED, GrantState.OFFERED],
    type: "multiCheckbox",
    value: formik.values.filters?.grantState || [],
    onChange: (selectedValues: string[]) => {
      formik.setFieldValue("filters.grantState", selectedValues);
    },
  });

  return (
    <VStack className="w-full gap-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">Event Details</h2>
        <p className="text-sm text-gray-500 mt-1">
          Define the repricing event parameters
        </p>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
        {/* Event Name */}
        <VStack className="gap-1">
          <Label className="text-sm font-normal">
            Event Name <span className="text-red-500">*</span>
          </Label>
          <Input
            type="text"
            placeholder="e.g. Q2 2026 Repricing"
            disabled={readonly}
            {...formik.getFieldProps("name")}
          />
          {formik.touched.name && formik.errors.name && (
            <Error text={formik.errors.name as string} />
          )}
        </VStack>

        {/* Effective Date */}
        <VStack className="gap-1">
          <Label className="text-sm font-normal">
            Effective Date <span className="text-red-500">*</span>
          </Label>
          <Input
            type="date"
            disabled={readonly}
            {...formik.getFieldProps("date")}
          />
          {formik.touched.date && formik.errors.date && (
            <Error text={formik.errors.date as string} />
          )}
          <p className="text-xs text-gray-400">
            Grant level: new price applies to all tranches of grants after this
            date. Vesting level: only tranches vesting on or after this date are
            updated.
          </p>
        </VStack>

        {/* New Strike Price */}
        <VStack className="gap-1">
          <Label className="text-sm font-normal">
            New Strike Price <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            disabled={readonly}
            {...formik.getFieldProps("newStrikePrice")}
          />
          {formik.touched.newStrikePrice && formik.errors.newStrikePrice && (
            <Error text={formik.errors.newStrikePrice as string} />
          )}
          <p className="text-xs text-gray-400">
            Must be lower than the current strike price of each selected grant
          </p>
        </VStack>

        {/* Repricing Type */}
        <VStack className="gap-2">
          <Label className="text-sm font-normal">
            Repricing Type <span className="text-red-500">*</span>
          </Label>
          <HStack className="gap-6">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="repricingType"
                value="VESTING"
                checked={formik.values.repricingType === "VESTING"}
                onChange={() =>
                  formik.setFieldValue("repricingType", "VESTING")
                }
                disabled={readonly}
                className="mt-1"
              />
              <VStack className="gap-0.5">
                <span className="text-sm font-medium">Vesting Level</span>
                <span className="text-xs text-gray-400">
                  Updates strike price per unvested tranche only
                </span>
              </VStack>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="repricingType"
                value="GRANT"
                checked={formik.values.repricingType === "GRANT"}
                onChange={() => formik.setFieldValue("repricingType", "GRANT")}
                disabled={readonly}
                className="mt-1"
              />
              <VStack className="gap-0.5">
                <span className="text-sm font-medium">Grant Level</span>
                <span className="text-xs text-gray-400">
                  Updates the grant&apos;s strike price for all future
                  calculations
                </span>
              </VStack>
            </label>
          </HStack>
          {formik.touched.repricingType && formik.errors.repricingType && (
            <Error text={formik.errors.repricingType as string} />
          )}
        </VStack>

        {/* Board Resolution Reference */}
        <VStack className="gap-1">
          <Label className="text-sm font-normal">
            Board Resolution Reference{" "}
            <span className="text-gray-400">(Optional)</span>
          </Label>
          <Input
            type="text"
            placeholder="e.g. BR/2025/04/001"
            disabled={readonly}
            {...formik.getFieldProps("boardResolutionReference")}
          />
        </VStack>

        {/* Supporting Documents */}
        <SupportingDocumentsField readonly={readonly} />

        {/* Reason for Repricing */}
        <VStack className="col-span-1 gap-1">
          <Label className="text-sm font-normal">Reason for Repricing</Label>
          <Select
            options={REPRICING_REASONS}
            placeholder="Select a reason..."
            disabled={readonly}
            {...formik.getFieldProps("reasonForRepricing")}
          />
        </VStack>
        {/* Notes — full width */}
        <VStack className="col-span-1 gap-1">
          <Label className="text-sm font-normal">Notes</Label>
          <TextArea
            placeholder="Optional notes about this repricing event"
            disabled={readonly}
            {...formik.getFieldProps("note")}
          />
        </VStack>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-5">
        <div className={`${readonly ? "col-span-1" : "col-span-2"}`}>
          <HStack className="items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Filter Grants
            </h3>
          </HStack>
          <p className="text-xs text-gray-400 mb-3">
            Restrict which grants this repricing applies to. Leave all filters
            empty to include all eligible grants.
          </p>
          <GenericFilterBar filters={filters} disableUpdate={false} />
          {!readonly && (
            <div className="mt-4 w-fit">
              <InfoBox message="All grants are selected by default. Use filters above to narrow the selection." />
            </div>
          )}
        </div>
        {readonly && s3url && (
          <div className="col-span-1 flex items-start">
            <RepricingReportDownload s3url={s3url} />
          </div>
        )}
      </div>

      <HStack className="justify-between mt-4">
        <ButtonSecondary className="h-8 px-4 py-1" onClick={onClose}>
          {readonly ? "Close" : "Cancel"}
        </ButtonSecondary>
        {!readonly && (
          <ButtonPrimary
            className="h-8 px-4 py-1"
            onClick={handleNext}
            loading={isLoading}
            disabled={isLoading}
            type="button"
          >
            Next
          </ButtonPrimary>
        )}
      </HStack>
    </VStack>
  );
}
